import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useCoverTemplate } from "./hooks/useCoverTemplates";
import CoverTemplateCard from "./CoverTemplateCard";
import CoverTemplateDialog from "./CoverTemplateDialog";
import { ICoverTemplate } from "./types/icover-template";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

const CoverProposalTemplate: React.FC = () => {
  const {
    coverTemplates,
    loading,
    error,
    addCoverTemplate,
    updateCoverTemplate,
    deleteCoverTemplate,
  } = useCoverTemplate();

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ICoverTemplate | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDialogSubmit = async (
    form: FormData,
    editing?: ICoverTemplate | null
  ) => {
    try {
      if (editing) {
        await updateCoverTemplate(editing.id, form);
        toast.success("Template updated successfully!");
      } else {
        await addCoverTemplate(form);
        toast.success("Template added successfully!");
      }
      setDialogOpen(false);
      setEditingTemplate(null);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to save template.";
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Delete this cover template?")) {
      try {
        await deleteCoverTemplate(id);
        toast.success("Template deleted successfully!");
      } catch (error: unknown) {
        const msg =
          (error as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail || "Failed to delete template.";
        setErrorMessage(msg);
        toast.error(msg);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Snackbar
        open={!!error || !!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert severity="error">{error || errorMessage}</Alert>
      </Snackbar>

      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          startIcon={<AddCircleOutlineOutlined />}
          variant="contained"
          onClick={() => {
            setEditingTemplate(null);
            setDialogOpen(true);
          }}
          sx={{
            backgroundColor: reduxTheme.primary_colors[400],
            color: reduxTheme.primary_colors[900],
          }}
        >
          Add Cover Template
        </Button>
      </Box>

      {coverTemplates.length === 0 ? (
        <Typography align="center" color="text.secondary">
          No cover templates available.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {coverTemplates.map((t) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={t.id}>
              <CoverTemplateCard
                template={t}
                onEdit={(temp) => {
                  setEditingTemplate(temp);
                  setDialogOpen(true);
                }}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <CoverTemplateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        editingTemplate={editingTemplate}
        loading={loading}
      />
    </Box>
  );
};

export default CoverProposalTemplate;
